import { useState } from 'react';
import noop from 'lodash/noop';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useMutation } from '@tanstack/react-query';
import cn from 'classnames';
import { SubmitHandler, useForm } from 'react-hook-form';
import { InputField, Button, BUTTON_THEMES } from 'ht-styleguide';

/* Api */
import { user } from '@api/apis';

/* Hooks */
import useNotice from '@hooks/useNotice';

/* Utils/Constants */
import styles from './resetPasswordPage.module.scss';
import { YupSchemaResolver } from '@constants/validations';

type YupFormProps = { email: string };
type ResetPasswordProps = { isMinimal?: boolean; onSignInClick?: BaseAnyFunction };

const ResetPasswordPage = ({ isMinimal = false, onSignInClick = noop }: ResetPasswordProps) => {
  const { back = 'login' } = useRouter().query;
  const { success, error } = useNotice();
  const [submitted, setSubmitted] = useState(false);
  /* Query */
  const resetPasswordMuation = useMutation({
    mutationFn: user.resetPassword,
    onSuccess: data => {
      if (data.err) {
        return error('Something went wrong');
      }

      return success('Reset Link sent!');
    },
  });

  /* Hooks */
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<YupFormProps>({ ...YupSchemaResolver(['email'], undefined, { email: '' }) });

  /* Methods */
  const resetFormAttributes: SubmitHandler<YupFormProps> = async ({ email }) =>
    resetPasswordMuation.mutate({ email }, { onSuccess: () => setSubmitted(true) });

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <h4 className="h4">Password Assistance</h4>
        <p className={cn('p1', styles.info)}>
          Enter the email address associated with your account and we’ll send you an email to reset your password.
        </p>
        <form className={styles.form} onSubmit={() => false}>
          <InputField label="Email Address" placeholder="Type something" error={errors.email?.message} type="text" {...register('email')} />

          <div className={styles.actions}>
            <Button
              theme={BUTTON_THEMES.PRIMARY}
              className={styles.button}
              onClick={handleSubmit(({ email }) => {
                resetFormAttributes({ email });
              })}
            >
              {/* Need to update this to check whether form has been submitted. */}
              {submitted ? 'Send Again' : 'Reset Password'}
            </Button>
            {!isMinimal ? (
              <Link href={`/${back}`} legacyBehavior>
                <Button className={styles.button} theme={BUTTON_THEMES.SECONDARY} type="button">
                  Sign In
                </Button>
              </Link>
            ) : (
              <Button className={styles.button} theme={BUTTON_THEMES.SECONDARY} type="button" onClick={onSignInClick}>
                Sign In
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
